import React from 'react'
import classNames from 'classnames'
import { format } from 'date-fns'
import { Image, Grid, GridItem } from '@deal/components'
import { Link } from 'react-router-dom'
import truncate from 'lodash/truncate'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import DepartmentExpertHeadline from '#src/components/DepartmentExpertHeadline'
import useBreakpoint from '#src/hooks/useBreakpoint'
import numeral from 'numeral'
import SliderWithScrollLock, {
  SlickSlider,
  CustomArrowProps
} from '#src/components/SliderWithScrollLock'
import useViewportSize from '#src/hooks/useViewportSize'
import { FeaturedReviewsComponentProps } from '..'
import Star from './star.svg'
import LeftArrow from './chevron-left.svg'
import RightArrow from './chevron-right.svg'
import ArrowRight from './arrow-right.svg'
import styles from './styles.css'

interface ArrowProps extends CustomArrowProps {
  gutterSize?: number
}

const PrevArrow: React.SFC<ArrowProps> = ({ onClick, className }) => {
  return (
    <div className={classNames(styles.prevArrow, className)} onClick={onClick}>
      <LeftArrow className={styles.chevronIcon} />
    </div>
  )
}

const NextArrow: React.SFC<ArrowProps> = ({ onClick, gutterSize, className }) => {
  return (
    <div
      className={classNames(styles.nextArrow, className)}
      style={{ marginRight: gutterSize && `${gutterSize}px` }}
      onClick={onClick}
    >
      <RightArrow className={styles.chevronIcon} />
    </div>
  )
}

const Reviews: React.SFC<FeaturedReviewsComponentProps> = ({
  forceFullGrid = false,
  className,
  header,
  subHeader,
  disableLinking = false,
  showMoreReviewsButton = true,
  featuredReviews,
  slidesToShow,
  gutterSize = 16,
  actions
}) => {
  const isLargerThanSm = useBreakpoint('sm')
  const isLargerThanMd = useBreakpoint('md')

  if (!slidesToShow) {
    slidesToShow = 1.5
  }
  if (isLargerThanMd && !slidesToShow) {
    slidesToShow = 3
  }

  let GridComponent = SidebarAwareGrid
  if (forceFullGrid) {
    GridComponent = Grid
  }

  let actualGutterSize = gutterSize
  if (isLargerThanMd) {
    actualGutterSize = 24
  }

  const sliderRef: React.RefObject<SlickSlider> = React.createRef()
  const sliderContRef: React.RefObject<HTMLDivElement> = React.createRef()
  const headerTruncateThreshold = isLargerThanSm ? 58 : 38
  const bodyTruncateTreshold = isLargerThanSm ? 230 : 150

  useViewportSize(() => {
    if (sliderContRef.current) {
      const sliderContainer = sliderContRef.current.getElementsByClassName('slick-track')[0]
      if (sliderContainer instanceof HTMLDivElement) {
        const cardDiv = sliderContainer.getElementsByClassName('slick-slide')[0]
        if (cardDiv instanceof HTMLDivElement) {
          const width = cardDiv.offsetWidth
          sliderContainer.style.marginLeft = isLargerThanMd
            ? '0'
            : `${(document.body.clientWidth - (width - 15)) / 2}px`
        }
      }
    }
  })

  return (
    <div className={classNames(styles.wrapper, className)}>
      <GridComponent>
        <GridItem col={2}>
          <div className={styles.border} />
        </GridItem>
      </GridComponent>
      <GridComponent>
        <GridItem col={2}>
          {header && <div className={styles.header}>{header}</div>}
          {subHeader && <div className={styles.subHeader}>{subHeader}</div>}
        </GridItem>
      </GridComponent>

      <GridComponent className={styles.reviewCarousel}>
        <GridItem col={2}>
          <div ref={sliderContRef}>
            <SliderWithScrollLock
              sliderRef={sliderRef}
              slidesToShow={slidesToShow}
              slidesToScroll={1}
              infinite={true}
              arrows={isLargerThanMd}
              prevArrow={isLargerThanMd && <PrevArrow gutterSize={actualGutterSize} />}
              nextArrow={isLargerThanMd && <NextArrow gutterSize={actualGutterSize} />}
              className={styles.carousel}
            >
              {featuredReviews.map(review => {
                if (!review.expert.expertProfile || !review.expert.expertProfile.profilePhoto) {
                  return null
                }

                return (
                  <div key={review.id}>
                    <div className={styles.reviewCard}>
                      <div className={styles.cardHeader}>
                        {disableLinking ? (
                          <Image
                            src={review.expert.expertProfile.profilePhoto.url}
                            sizes="(min-width: 768px) 200px, 150px"
                            className={styles.cardImage}
                          />
                        ) : (
                          <Link to={`/experts/${review.expert.vanityId || review.expert.id}`}>
                            <Image
                              src={review.expert.expertProfile.profilePhoto.url}
                              sizes="(min-width: 768px) 200px, 150px"
                              className={styles.cardImage}
                            />
                          </Link>
                        )}

                        <div className={styles.cardHeaderContent}>
                          <div className={styles.cardHeaderNameContainer}>
                            {disableLinking ? (
                              <>
                                <div className={styles.cardHeaderExpert}>
                                  {review.expert.shortName}
                                </div>

                                <div className={styles.cardHeaderRatingContainer}>
                                  {numeral(review.rating).format('0.0')}
                                  <Star className={styles.cardHeaderRatingStar} />
                                </div>
                              </>
                            ) : (
                              <>
                                <Link to={`/experts/${review.expert.vanityId || review.expert.id}`}>
                                  <div className={styles.cardHeaderExpert}>
                                    {review.expert.shortName}
                                  </div>
                                </Link>
                                <Link to={`/experts/${review.expert.vanityId || review.expert.id}`}>
                                  <div className={styles.cardHeaderRatingContainer}>
                                    {numeral(review.rating).format('0.0')}
                                    <Star className={styles.cardHeaderRatingStar} />
                                  </div>
                                </Link>
                              </>
                            )}
                          </div>
                          <div className={styles.cardHeaderExpertTitle}>
                            <DepartmentExpertHeadline
                              departmentType={review.expert.department.type}
                            />
                          </div>
                        </div>
                      </div>
                      <div className={styles.cardContent}>
                        {review.headline && (
                          <div className={styles.reviewHeadline}>
                            {truncate(review.headline, {
                              length: headerTruncateThreshold
                            })}
                          </div>
                        )}
                        {review.comments && (
                          <div className={styles.reviewBody}>
                            <span>
                              "
                              {truncate(review.comments, {
                                length: bodyTruncateTreshold
                              })}
                              "&nbsp;
                            </span>
                            {review.comments.length > bodyTruncateTreshold && !disableLinking && (
                              <Link
                                to={`/experts/${review.expert.vanityId ||
                                  review.expert.id}?reviewId=${review.id}#reviews`}
                              >
                                <span className={styles.reviewExpand}>expand</span>
                              </Link>
                            )}

                            {review.consumerName && (
                              <div className={styles.consumerName}>
                                - {review.consumerName}, {format(review.reviewedAt, 'LLL d')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </SliderWithScrollLock>
          </div>
        </GridItem>
      </GridComponent>

      {(showMoreReviewsButton || actions) && (
        <GridComponent>
          <GridItem col={2} className={styles.actions}>
            {actions ? (
              <>{actions}</>
            ) : (
              <Link to="/reviews#reviews">
                <div className={styles.moreReviews}>
                  View All Reviews <ArrowRight height={12} width={12} />
                </div>
              </Link>
            )}
          </GridItem>
        </GridComponent>
      )}
    </div>
  )
}

export default Reviews
